// ════════════════════════════════════════════════════════════
// /compte — Sprint 6.5 (paramètres du compte, tous rôles)
// ════════════════════════════════════════════════════════════
// Self-service : chaque utilisateur change SON propre mot de passe.
// Pas besoin du serveur service_role — Supabase autorise un user
// authentifié à modifier son propre mot de passe.
// Sécurité : on exige le mot de passe actuel (re-auth) avant de
// le changer.
// ════════════════════════════════════════════════════════════
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { KeyRound, Eye, EyeOff, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { ROLE_LABELS } from '@/types/auth';

export const Route = createFileRoute('/_authed/compte/')({
  component: ComptePage,
});

const MIN_LEN = 6;

function ComptePage() {
  const { session, profile } = useAuth();
  const email = session?.user.email ?? '';
  const role = profile?.role ?? null;

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
  };

  const handleSubmit = async () => {
    if (!email) {
      toast.error('Session introuvable, reconnecte-toi.');
      return;
    }
    if (next.length < MIN_LEN) {
      toast.error(`Le nouveau mot de passe doit faire au moins ${MIN_LEN} caractères.`);
      return;
    }
    if (next !== confirm) {
      toast.error('La confirmation ne correspond pas.');
      return;
    }
    if (next === current) {
      toast.error('Le nouveau mot de passe doit être différent de l’actuel.');
      return;
    }

    setBusy(true);
    try {
      // 1. Vérifie le mot de passe actuel (re-authentification).
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: current,
      });
      if (signInError) {
        toast.error('Mot de passe actuel incorrect.');
        return;
      }

      // 2. Applique le nouveau mot de passe.
      const { error: updateError } = await supabase.auth.updateUser({ password: next });
      if (updateError) {
        toast.error(`Échec : ${updateError.message}`);
        return;
      }

      toast.success('Mot de passe modifié.');
      reset();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erreur inattendue.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-4">
      <header className="flex items-center gap-3">
        <span className="bg-primary/10 text-primary flex size-10 items-center justify-center rounded-lg">
          <User className="size-5" />
        </span>
        <div>
          <h1 className="text-foreground text-xl font-semibold sm:text-2xl">Mon compte</h1>
          <p className="text-muted-foreground text-sm">Paramètres personnels</p>
        </div>
      </header>

      {/* Infos compte */}
      <Card>
        <CardContent className="space-y-1.5 py-4 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Email</span>
            <span className="text-foreground truncate font-mono text-xs">{email || '—'}</span>
          </div>
          {role && (
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Rôle</span>
              <span className="text-foreground">{ROLE_LABELS[role]}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Changer mot de passe */}
      <Card>
        <CardContent className="space-y-4 py-4">
          <div className="text-muted-foreground flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide">
            <KeyRound className="size-3.5" />
            Changer mon mot de passe
          </div>

          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="current">Mot de passe actuel</Label>
              <Input
                id="current"
                type={show ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                disabled={busy}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="next">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="next"
                  type={show ? 'text' : 'password'}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  autoComplete="new-password"
                  disabled={busy}
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="text-muted-foreground hover:text-foreground absolute right-2.5 top-1/2 -translate-y-1/2"
                  aria-label={show ? 'Masquer' : 'Afficher'}
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              <p className="text-muted-foreground text-[11px]">Au moins {MIN_LEN} caractères.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirmer le nouveau mot de passe</Label>
              <Input
                id="confirm"
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                disabled={busy}
              />
            </div>

            <Button type="submit" className="w-full gap-1.5" disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
              Mettre à jour le mot de passe
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
